import DisasterAreaForm from './fragments/disaster-area-form/DisasterAreaForm.vue'
import Authorization from './fragments/authorization/Authorization'
import DisasterSubTypesService from './disaster-subtypes-service'
import DisasterAreasService from './disaster-areas-service'
import DisasterTypesService from './disaster-types-service'
import DisasterProvidersService from './disaster-provider-services'
import config from './config'
import Vue from '@/common/prepared-vue.js'
import {EventBus} from '@/common/event-bus'
import store from '@/store/store'

/**
 * DisasterAvoidPolygons class
 */
class DisasterAvoidPolygons {

  /**
   * DisasterAvoidPolygons constructor
   */
  constructor (vueInstance) {
    this.vueInstance = vueInstance

    this.providers = []
    this.disasterAreaFormOpen = false
    this.authorizationKey = null
    this.authorizationComponentLoaded = false

    // Load disaster type and sub type options
    DisasterTypesService.query().then(response => {
      this.disasterTypes = response.data
    })
    DisasterSubTypesService.query().then(response => {
      this.disasterSubTypes = response.data
    })
    DisasterProvidersService.query().then((response) => {
      this.providers = response.data
      let provider_list = this.providers.map(r => {
        return {id: r.id, name: r.name}
      })
      store.commit('providers', provider_list)
    })
  }

  /**
   * Remove avoid polygons form options.
   * The avoid-polygons (disaster areas)
   * are stored in the disaster portal back-end service
   * and are automatically added in the request.
   */
  afterGetRouteOptions (options) {
    if (options.options && options.options.avoid_polygons) {
      delete options.options.avoid_polygons
    }
  }

  /**
   * Adjust the routing args before a request is made.
   * For disaster areas, an specific service is being used for routing, instead of
   * default ors api.
   * @param {*} args
   */
  routingArgsCreated (args) {
    args.service = `${config.baseDisasterUrl}${config.portalDirectionsEndpoint}`
    args.customHeaders = {}
    args.customHeaders[config.portalCustomHeaderName] = config.orsKey
    args.portal_options = {
      debug: false,
      return_areas_in_response: false,
      bounds_looseness: 200
    }
  }

  /**
   * Adjust the isochrones args before a request is made.
   * For disaster areas, an specific service is being used for isochrones, instead of
   * default ors api.
   * @param {*} args
   */
  isochronesArgsCreated (args) {
    args.service = `${config.baseDisasterUrl}${config.portalIsochronesEndPoint}`
    args.customHeaders = {}
    args.customHeaders[config.portalCustomHeaderName] = config.orsKey
    args.portal_options = {
      debug: false,
      return_areas_in_response: false,
      bounds_looseness: 200
    }
  }



  /**
   * Load polygons
   * @returns {Array} polygons
   */
  mapReady (hookData) {
    this.loadDisasterAreas()
    if (!this.authorizationComponentLoaded) {
      this.addAuthorizationDialog(hookData)
    }
  }

  loadDisasterAreas () {
    let bbox = store.getters.mapBounds
    // Weh the user moves the map while a disaster area form is open, do not
    // load other disaster areas, because it will trigger the removal of the
    // for that is open
    if (!this.disasterAreaFormOpen) {
      let bboxStr = `${bbox.rect.min_lon},${bbox.rect.max_lat},${bbox.rect.max_lon},${bbox.rect.min_lat}`
      DisasterAreasService.query({bbox: bboxStr}).then((response) => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          EventBus.$emit('loadAvoidPolygons', response.data)
        }
      })
    }
  }

  /**
   * Add the edit polygon properties button to the popup displayed
   * when the polygon is clicked
   * @param {*} hookData
   */
  beforeShowAvoidPolygonPopup (hookData) {
    // To avoid content multiple times
    // we check it the component was not already added
    let buttons = hookData.popupHtmlFragment.querySelectorAll('a')


    // If the VueJS component was not already added
    if (buttons.length === 2) {
      let editEl = document.createElement('a')
      editEl.onclick = () => {
        this.showDisasterAreaForm(hookData)
      }
      editEl.title = this.vueInstance.$store.getters.authorizationKey ? this.vueInstance.$t('disasterAvoidPolygons.editProperties') : ''
      editEl.innerText = this.vueInstance.$store.getters.authorizationKey ? 'edit' : 'info'
      editEl.className = 'material-icons leaflet-draw-custom-actions'

      // Append the html node to the html fragment
      hookData.popupHtmlFragment.appendChild(editEl)
    }
  }

  /**
   * Handle avoid polygon created, opening the AvoidPolygonForm
   * @param {Object} hookData
   */
  avoidPolygonCreated (hookData) {
    // hookData has the following structure {polygon: Object, map: Object, context: Object}
    return this.showDisasterAreaForm(hookData)
  }

  addAuthorizationDialog (hookData) {
    let context = this
    var AuthorizationClass = Vue.extend(Authorization)
    var instance = new AuthorizationClass({ i18n: this.vueInstance.$i18n, store: this.vueInstance.$store})
    instance.$on(['cancel'], () => {
      console.log('cancelled')
      context.vueInstance.showInfo(context.vueInstance.$t('disasterAvoidPolygons.disasterAreaUsageInfo'), {timeout: 0})
    })
    instance.$on(['saved'], (authorizationKey) => {
      store.commit('authorizationKey', authorizationKey)
      context.vueInstance.showInfo(context.vueInstance.$t('disasterAvoidPolygons.disasterAreaUsageInfo'), {timeout: 0})
    })
    instance.$mount() // pass nothing
    hookData.context.$refs.customMapControlsContainer.appendChild(instance.$el)
    this.authorizationComponentLoaded = true
  }

  /**
   * Show avoid polygon from
   * @param {*} hookData
   */
  showDisasterAreaForm (hookData) {
    return new Promise((resolve, reject) => {
      var ComponentClass = Vue.extend(DisasterAreaForm)
      let props = { polygonLayer: hookData.polygon, disasterTypes: this.disasterTypes, disasterSubTypes: this.disasterSubTypes}

      // Instantiate the DisasterAreaForm
      var instance = new ComponentClass({ propsData: props, i18n: this.vueInstance.$i18n, store: this.vueInstance.$store})
      instance.$on(['cancel'], () => {
        this.cancelPolygonSaving(hookData)
        reject(false)
      })
      instance.$on(['saved'], () => {
        this.disasterAreaFormOpen = false
        resolve (hookData)
      })

      instance.$mount() // pass nothing

      // Set popup options
      let popupOptions = {closeButton: false, closeOnEscapeKey: false, closeOnClick: false, keepInView: true, autoClose: false}

      // Create and show popup
      hookData.polygon.bindPopup(instance.$el, popupOptions).openPopup()
      this.disasterAreaFormOpen = true
    })
  }

  /**
   * Cancel avoid polygon creation
   * @param {Object} hookData
   */
  cancelPolygonSaving (hookData) {
    hookData.polygon.closePopup()
    let polygonData = hookData.polygon.toGeoJSON()
    polygonData.properties = polygonData.properties || {}

    // If the polygon has an id, then the plugin is in editing mode
    // wand we should not remove it if the cancel btn is clicked
    if (!polygonData.properties.id) {
      hookData.map.removeLayer(hookData.polygon)
    }
    this.disasterAreaFormOpen = false
  }

  /**
   * Remove the the avoid polygon from the api/storage
   * @param {Object} hookData
   */
  avoidPolygonRemoved (hookData) {
    return new Promise((resolve) => {
      let polygonGeoJson = hookData.polygon.toGeoJSON()
      let polygonModel = DisasterAreasService.newModelInstance(polygonGeoJson)
      // geojson objects don't store id in the root, but in properties
      polygonModel.id = polygonModel.properties.id
      let context = this
      polygonModel.$destroy().then(() => {
        context.vueInstance.showSuccess(context.vueInstance.$t('disasterAvoidPolygons.avoidAreaDeleted'))
        hookData.polygon.closePopup()
        hookData.map.removeLayer(hookData.polygon)
        resolve({})
      }).catch(response => {
        let statusesMsg = context.vueInstance.$t('disasterAvoidPolygons.statuses')
        if (response.status && statusesMsg[response.status]) {
          context.vueInstance.showError(statusesMsg[response.status])
        }
      })
    })
  }

  /**
   * Save the avoid polygon edited in the back-end
   * @param {*} hookData
   */
  avoidPolygonEdited (hookData) {
    let polygonGeoJson = hookData.polygon.toGeoJSON()
    let polygonModel = DisasterAreasService.newModelInstance(polygonGeoJson)
    polygonModel.id = polygonModel.properties.id

    let context = this
    polygonModel.$save().then(() => {
      context.vueInstance.showSuccess(context.vueInstance.$t('disasterAvoidPolygons.avoidAreaSaved'))
      hookData.polygon.closePopup()
    }).catch(response => {
      let statusesMsg = context.vueInstance.$t('disasterAvoidPolygons.statuses')
      if (response.status && statusesMsg[response.status]) {
        context.vueInstance.showError(statusesMsg[response.status])
      }
    })
  }

  /**
   * Customize avoid polygon drawing tool btn labels
   * @param {Object} translations
   */
  avoidPolygonBtnTranslations (translations) {
    translations.avoidPolygon = this.vueInstance.$t('disasterAvoidPolygons.createADisasterPolygon')
    translations.avoidRectangle = this.vueInstance.$t('disasterAvoidPolygons.createADisasterRectangle')
  }
}
// export the AppHooks json builder class
export default DisasterAvoidPolygons
